import React from 'react';
import NamedBox from '../atoms/NamedBox';
import { COLOR_BLACK, COLOR_WHITE, COLOR_BLUE, COLOR_DARK_BLUE, COLOR_PURPLE } from '../styles/colors';
import Accordion from '../atoms/Accordion';
import ListItem from '../atoms/ListItem';
import { PlayerCollegeMPH } from '../types/PlayerCollegeMPH';
import { Grid, makeStyles, Theme } from '@material-ui/core';
import ProgressLine from '../atoms/ProgressLine';
import Tooltip from '../atoms/Tooltip';
import PFFIcon from '../icons/PFFIcon'
import SpeedIcon from '../icons/SpeedIcon';
import DistanceIcon from '../icons/DistanceIcon';
import FootballHelmetIcon from '../icons/FootballHelmetIcon';
import ArrowUpCircleIcon from '../icons/ArrowUpCircleIcon';
import PercentCircleIcon from '../icons/PercentCircleIcon';
import SpeedRecruitIcon from '../icons/SpeedRecruitIcon';
import SpeedometerCircleIcon from '../icons/SpeedometerCircleIcon';
import SpeedometerIcon from '../icons/SpeedometerIcon';
import StarSquareIcon from '../icons/StarSquareIcon';
import LinkIcon from '../icons/LinkIcon';
const BETTER_THAN_TOOLTIP_TEXT = 'Better than D-I position';

interface PFFPlayerInGameRatingProps {
  collegeInGameStats: PlayerCollegeMPH[];
  linkPffUrl?: string;
  className?: string;
  isBestAvailable?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  headerName: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
    width: '100%',
    marginRight: theme.spacing(2),
    fontWeight: 700,
  },
  headerIcon: {
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '44px',
    height: '44px',
    marginRight: theme.spacing(1.5),
    borderRadius: '50%',
    border: `2px solid ${COLOR_BLACK}`,
    backgroundColor: COLOR_PURPLE,
    fontSize: theme.typography.pxToRem(14),
    color: COLOR_WHITE,
    
    [theme.breakpoints.down('md')]: {
      width: 34,
      height: 34,
      fontSize: theme.typography.pxToRem(12),
    },
  },
  headerText: {
    flexGrow: 1,
  },
  pffIcon: {
    flexShrink: 0,
    width: 142,
    maxWidth: '44%',

    '& svg': {
      display: 'block',
    },
  },
  gridContainer: {
    alignItems: 'center',
  },
  rowContent: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    justifyContent: 'space-between',
    
    [theme.breakpoints.down('md')]: {
      display: 'inline-flex',
    },
  },
  gridIcon: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: theme.spacing(1),
  },
  progressLineWrapper: {
    width: '100%',
  },
  progressLine: {
    maxWidth: 350,
    padding: 0,
    marginLeft: 'auto',
  },
  progressLineBar: {
    backgroundColor: COLOR_PURPLE,
  },
  listArrowIcon: {
    '& .arrow-icon-background path': {
      fill: 'transparent',
    },

    '& .arrow-icon-color path': {
      stroke: COLOR_BLACK,
    },
  },
  purpleArrowIcon: {
    '& .arrow-icon-background path': {
      fill: COLOR_PURPLE,
    },
  },
  listPercentIcon: {
    '& .percent-icon-background path': {
      fill: COLOR_PURPLE,
    },
  },
  badgeWrap: {
    marginRight: 10,
    width: '45px',
  },
  badgeVal: {
    color: COLOR_BLACK,
  },
  linkUrl: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    display: 'inline-block',
    maxWidth: 200,
    minWidth: 200,
    fontWeight: 700,
    color: COLOR_BLUE,

    '&:hover': {
      color: COLOR_DARK_BLUE,
    },
  },
}), {
  name: 'PFFPlayerInGameRating',
});

export default function PFFPlayerInGameRating(props: PFFPlayerInGameRatingProps) {
  const {
    collegeInGameStats,
    linkPffUrl,
    isBestAvailable,
    className,
  } = props;

  const classes = useStyles();

  const sortedCollegeStats = [...collegeInGameStats].sort(
    (a, b) => (b.year ?? 0) - (a.year ?? 0)
  );

  let careerBest = sortedCollegeStats.reduce<PlayerCollegeMPH | null | undefined>(
    (best, season) =>
      (season.mph ?? 0) > (best?.mph ?? 0) ? season : best,
    null
  );


  if (isBestAvailable) {
    careerBest = sortedCollegeStats?.find((stats: any) => stats.isBest);
  }

  return (
    <NamedBox
      className={className}
      headerNameClassName={classes.headerName}
      name={
        <>
          <div className={classes.headerIcon}>MPH</div>
          <span className={classes.headerText}>College In-Game Rating</span>
        </>
      }
      headerContent={<PFFIcon className={classes.pffIcon}/>}
    >
      {sortedCollegeStats.length > 0 &&
        sortedCollegeStats.map((season: PlayerCollegeMPH, index) => {
          const {
            year = 0,
            mph = 0,
            mphPercentile = 0,
            distanceTraveled = 0,
            mphPositionRank = 0,
            mphPosition = '',
            timesExceeded20 = 0,
            averageTop5MPH = 0,
            ncaaMPHRank = 0,
            ncaaMPHPercentile = 0,
          } = season;

          return (
            <Accordion
              key={year || index}
              summary={`Film Year: ${year} (${mphPosition})`}
              expanded={index === 0}
            >
              {/* Season Max MPH */}
              <ListItem
                icon={SpeedIcon}
                title='Season Max MPH:'
              >
                <div className={classes.rowContent}>
                  <b>{mph}</b>
                </div>
              </ListItem>

              {/* Play Distance */}
              <ListItem
                icon={DistanceIcon}
                title='Play Distance:'
              >
                <div className={classes.rowContent}>
                  <b>{distanceTraveled} yds.</b>
                </div>
              </ListItem>

              {/* Position MPH % */}
              <ListItem
                icon={FootballHelmetIcon}
                title="Position MPH %:"
              >
                <Grid container className={classes.gridContainer}>
                  <Grid item lg={4} sm={5} xs={12}>
                    <div className={classes.rowContent}>
                      <b>{mphPercentile}%</b>
  
                      {mphPercentile >= 75 && (
                        <div className={classes.gridIcon}>
                          <Tooltip title={BETTER_THAN_TOOLTIP_TEXT}>
                            <SpeedRecruitIcon className={'purple'} padding={'0'} />
                          </Tooltip>
                        </div>
                      )}
                    </div>
                  </Grid>
                  <Grid item lg xs>
                    <div className={classes.progressLineWrapper}>
                      <ProgressLine
                        className={classes.progressLine}
                        progressBarClassName={classes.progressLineBar}
                        value={mphPercentile}
                      />
                    </div>
                  </Grid>
                </Grid>
              </ListItem>

              {/* Position Rank */}
              <ListItem
                icon={ArrowUpCircleIcon}
                iconClassName={classes.listArrowIcon}
                title='Position Rank:'
              >
                <div className={classes.rowContent}>
                  <b>{mphPositionRank}</b>
                </div>
              </ListItem>

              {/* NCAA MPH % */}
              <ListItem
                icon={PercentCircleIcon}
                iconClassName={classes.listPercentIcon}
                title='NCAA MPH %:'
              >
                <Grid container className={classes.gridContainer}>
                  <Grid item lg={4} sm={5} xs={12}>
                    <div className={classes.rowContent}>
                      <b>{ncaaMPHPercentile}%</b>
                    </div>
                  </Grid>
                  <Grid item lg xs>
                    <div className={classes.progressLineWrapper}>
                      <ProgressLine
                        className={classes.progressLine}
                        progressBarClassName={classes.progressLineBar}
                        value={ncaaMPHPercentile}
                      />
                    </div>
                  </Grid>
                </Grid>
              </ListItem>

              {/* NCAA Rank */}
              <ListItem
                icon={ArrowUpCircleIcon}
                iconClassName={classes.purpleArrowIcon}
                title='NCAA Rank:'
              >
                <div className={classes.rowContent}>
                  <b>{ncaaMPHRank}</b>
                </div>
              </ListItem>

              {/* Top 5 MPH Avg */}
              <ListItem
                icon={SpeedometerCircleIcon}
                title="Top 5 MPH Avg:"
              >
                <div className={classes.rowContent}>
                  <b>{averageTop5MPH || 'N/A'}</b>
                </div>
              </ListItem>

              {/* Times 20+ MPH */}
              <ListItem
                icon={SpeedometerIcon}
                title="Times 20+ MPH:"
              >
                <div className={classes.rowContent}>
                  <b>{timesExceeded20 || 'N/A'}</b>
                </div>
              </ListItem>

              {/* Career Best */}
              <ListItem
                icon={StarSquareIcon}
                title="Career Best MPH:"
              >
                <div className={classes.rowContent}>
                  <b>
                    {careerBest?.mph ?? 0}
                    {careerBest?.year ? ` (${careerBest.year})` : ''}
                  </b>
                </div>
              </ListItem>

              {/* Player URL */}
              {linkPffUrl && (
                <ListItem
                  icon={LinkIcon}
                  title="Player PFF URL:"
                >
                  <div className={classes.rowContent}>
                    <a
                      className={classes.linkUrl}
                      href={linkPffUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {linkPffUrl}
                    </a>
                  </div>
                </ListItem>
              )}
            </Accordion>
          );
        })}
    </NamedBox>
  );
}
